// ============================================================================
// Crop Utilities — pure math helpers for image crop editor
// ============================================================================

export const MIN_CROP_PERCENT = 5;

export interface CropRect {
  topLeftX: number;
  topLeftY: number;
  bottomRightX: number;
  bottomRightY: number;
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Given the displayed image element rect and its natural dimensions, compute
 * the visible sub-rect when the image uses object-fit: contain (letterboxing).
 */
export function getContainedRect(imageRect: DOMRect, naturalWidth: number, naturalHeight: number) {
  return getContainedRectFromSize(imageRect.width, imageRect.height, naturalWidth, naturalHeight);
}

/**
 * Same as getContainedRect but accepts explicit width/height instead of DOMRect.
 * Use this with offsetWidth/offsetHeight (layout-space, unaffected by CSS transforms)
 * for positioning overlays inside a transform wrapper.
 */
export function getContainedRectFromSize(
  displayWidth: number,
  displayHeight: number,
  naturalWidth: number,
  naturalHeight: number,
) {
  const displayAspect = displayWidth / displayHeight;
  const naturalAspect = naturalWidth / naturalHeight;

  let visibleW: number;
  let visibleH: number;
  if (naturalAspect > displayAspect) {
    visibleW = displayWidth;
    visibleH = displayWidth / naturalAspect;
  } else {
    visibleH = displayHeight;
    visibleW = displayHeight * naturalAspect;
  }

  const offsetX = (displayWidth - visibleW) / 2;
  const offsetY = (displayHeight - visibleH) / 2;

  return { offsetX, offsetY, visibleW, visibleH };
}

/**
 * Convert a mouse client position to percentage coordinates (0–100) of the
 * natural image, accounting for object-fit: contain letterboxing.
 */
export function clientToPercent(
  clientX: number,
  clientY: number,
  imageRect: DOMRect,
  naturalWidth: number,
  naturalHeight: number,
): { x: number; y: number } {
  const { offsetX, offsetY, visibleW, visibleH } = getContainedRect(imageRect, naturalWidth, naturalHeight);

  const relX = clientX - imageRect.left - offsetX;
  const relY = clientY - imageRect.top - offsetY;

  return {
    x: clamp((relX / visibleW) * 100, 0, 100),
    y: clamp((relY / visibleH) * 100, 0, 100),
  };
}

/**
 * Compute the output pixel dimensions from a percentage crop rect.
 */
export function computeOutputDimensions(
  crop: CropRect,
  naturalWidth: number,
  naturalHeight: number,
): { width: number; height: number } {
  const w = Math.round(((crop.bottomRightX - crop.topLeftX) / 100) * naturalWidth);
  const h = Math.round(((crop.bottomRightY - crop.topLeftY) / 100) * naturalHeight);
  return { width: Math.max(w, 0), height: Math.max(h, 0) };
}

/**
 * Adjust a crop rect to match the given aspect ratio, anchored from top-left.
 * ratio is width/height (e.g., 16/9 = 1.778).
 */
export function enforceAspectRatio(
  crop: CropRect,
  ratio: number | null,
  naturalWidth: number,
  naturalHeight: number,
): CropRect {
  if (ratio == null) return crop;

  const cropW = crop.bottomRightX - crop.topLeftX;
  const cropH = crop.bottomRightY - crop.topLeftY;

  // Convert percentage-space aspect to pixel-space
  const pixelW = (cropW / 100) * naturalWidth;
  const pixelH = (cropH / 100) * naturalHeight;
  const currentRatio = pixelW / pixelH;

  let newW = cropW;
  let newH = cropH;

  if (currentRatio > ratio) {
    // Too wide — shrink width
    const targetPixelW = pixelH * ratio;
    newW = (targetPixelW / naturalWidth) * 100;
  } else {
    // Too tall — shrink height
    const targetPixelH = pixelW / ratio;
    newH = (targetPixelH / naturalHeight) * 100;
  }

  // Clamp to image bounds
  const brX = Math.min(crop.topLeftX + newW, 100);
  const brY = Math.min(crop.topLeftY + newH, 100);

  return {
    topLeftX: crop.topLeftX,
    topLeftY: crop.topLeftY,
    bottomRightX: brX,
    bottomRightY: brY,
  };
}

export interface AspectPreset {
  label: string;
  ratio: number | null;
  group: 'standard' | 'gc';
}

export const ASPECT_PRESETS: AspectPreset[] = [
  // Standard
  { label: 'Free', ratio: null, group: 'standard' },
  { label: '16:9', ratio: 16 / 9, group: 'standard' },
  { label: '4:3', ratio: 4 / 3, group: 'standard' },
  { label: '3:2', ratio: 3 / 2, group: 'standard' },
  { label: '1:1', ratio: 1, group: 'standard' },
  { label: '9:16', ratio: 9 / 16, group: 'standard' },
  { label: '3:4', ratio: 3 / 4, group: 'standard' },
  { label: '2:3', ratio: 2 / 3, group: 'standard' },
  // GC
  { label: 'Comic Cover', ratio: 540 / 835, group: 'gc' },
  { label: 'Hero 16:9', ratio: 16 / 9, group: 'gc' },
  { label: 'Hero 1:1', ratio: 1, group: 'gc' },
  { label: 'Hero 3:2', ratio: 3 / 2, group: 'gc' },
  { label: 'Mobile Hero', ratio: 9 / 16, group: 'gc' },
];
